<template>
	<div>
		<v-app-bar flat app absolute color="white" id="header-bar" role="banner" :aria-label="$t('aria.main_application_header')">
			<!-- Navigation drawer toggle -->
			<v-app-bar-nav-icon
				color="primary"
				@click="handleMiniDrawer"
				:aria-label="$t('aria.toggle_navigation_menu')"
				role="button"
				tabindex="0"
				@keyup.enter="handleMiniDrawer"
				@keyup.space.prevent="handleMiniDrawer"
			></v-app-bar-nav-icon>

			<v-toolbar-title tabindex="0">Hirsch</v-toolbar-title>
			<!-- keyboard focusable -->

			<v-spacer></v-spacer>

			<!-- Status indicator: use aria-live to notify screen readers -->
			<div class="mr-9 d-flex" v-if="status?.process?.evaluating" aria-live="polite" aria-atomic="true" role="status">
				<div class="mr-3 text-body-1 text-orange text-bold">
					{{ $t('calculating_data') }}
				</div>
				<v-img class="spin" contain src="@/assets/progressIcon.svg" alt="Loading indicator" />
			</div>

			<div class="mr-9 d-flex" v-if="evaluationComplete" aria-live="polite" aria-atomic="true" role="status">
				<div class="mr-3 text-body-1 text-green text-bold">
					{{ $t('calculation_done') }}
				</div>
				<v-icon color="green" aria-hidden="true">mdi-check-circle</v-icon>
			</div>

			<auto-update-top-bar v-if="$route && $route.name === 'Planning' && isLoggedIn" :class="{ 'mr-16': !showCoreTopBar }" />

			<core-top-bar v-if="isLoggedIn && user.permissions['core.edit']" />

			<!-- User menu -->
			<v-menu offset-y data-testid="menu" v-if="isLoggedIn" role="menu" :aria-label="$t('aria.user_menu')">
				<template v-slot:activator="{ props }">
					<v-btn icon v-bind="props" :aria-label="$t('aria.user_account_menu')">
						<v-icon color="primary" aria-hidden="true">mdi-account</v-icon>
					</v-btn>
				</template>
				<v-list role="menu">
					<v-list-item>
						<v-btn variant="text" @click="$router.push('/myProfile')" role="menuitem" tabindex="0">
							{{ $t('navigation_profile') }}
						</v-btn>
					</v-list-item>

					<v-list-item>
						<v-btn v-if="isLoggedIn" @click="handleLogout" data-testid="logout" variant="text" role="menuitem" tabindex="0">
							{{ $t('action_logout') }}
						</v-btn>
					</v-list-item>
				</v-list>
			</v-menu>

			<!-- Language picker shown if not logged in -->
			<v-row v-if="!isLoggedIn">
				<v-col class="py-0">
					<language-picker :value="selected" @input="(v) => (selected = v)" type="select" :list="list"></language-picker>
				</v-col>
			</v-row>
		</v-app-bar>
		<!--Export Modal (Date Range) -->
		<v-dialog v-model="exportDialog" max-width="500px" persistent>
			<v-card>
				<v-card-title>
					<span class="headline">{{ $t('planning_download_duty') }}</span>
				</v-card-title>

				<v-card-text>
					<v-container>
						<v-form ref="exportForm" v-model="isExportFormValid">
							<v-row>
								<v-col cols="12" sm="6">
									<DateEntryForm
										v-model="exportDateRange.from"
										:rules="planningDataExportDateRule"
										:label="$t('user_edit_from')"
										@change="validateExportDateRange"
										@blur="validateExportDateRange"
									/>
								</v-col>

								<v-col cols="12" sm="6">
									<DateEntryForm
										v-model="exportDateRange.to"
										:rules="planningDataExportDateRule"
										:label="$t('user_edit_to')"
										@change="validateExportDateRange"
										@blur="validateExportDateRange"
									/>
								</v-col>

								<v-col cols="12" v-if="exportDateRangeError">
									<v-alert type="error" dense>
										{{ exportDateRangeError }}
									</v-alert>
								</v-col>
							</v-row>
						</v-form>
					</v-container>
				</v-card-text>

				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="closeExportModal">{{ $t('action_cancel') }}</v-btn>
					<v-btn color="primary" @click="download" :loading="isDownloading" :disabled="isDownloadDisabled">
						{{ exportButtonLabel }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		>
	</div>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import LanguagePicker from '../common/locales/views/LanguagePickerV3.vue';
import autoUpdateTopBar from './autoUpdateTopBar.vue';
import CoreTopBar from './CoreTopBar.vue';
import DateEntryForm from '../../components/DateAndTime/DateEntryForm/DateEntryForm.vue';
import fileDownloadMixin from '../mixins/fileDownloadMixin.js';

export default {
	name: 'TopBar',
	components: {
		DateEntryForm,
		LanguagePicker,
		autoUpdateTopBar,
		CoreTopBar,
	},
	mixins: [fileDownloadMixin],
	data() {
		return {
			evaluationComplete: false,
			debounceTimer: null,
			doneTimer: null,
			hasMounted: false,
			prevEvaluating: false,
			exportDialog: false,
			exportFormat: 'csv',
			exportDateRange: {
				from: '',
				to: '',
			},
			exportDateRangeError: null,
			isExportFormValid: false,
			isDownloading: false,
		};
	},
	props: {
		value: Boolean,
	},
	computed: {
		...mapState({
			user: (state) => state.auth.user,
			list: (state) => state.locales.list,
			current: (state) => state.profile.locale,
			status: (state) => state.core.status,
		}),
		...mapGetters({ isLoggedIn: 'auth/isLoggedIn' }),
		selected: {
			get() {
				return this.current.key;
			},
			set(value) {
				this.setLocale(value);
			},
		},
		showCoreTopBar() {
			return this.isLoggedIn && !!this.user?.permissions?.['core.edit'];
		},
		exportButtonLabel() {
			return this.exportFormat === 'xlsx' ? this.$t('planning_download_duty_xlsx') : this.$t('planning_download_duty_csv');
		},
		planningDataExportDateRule() {
			return [(v) => !!v || this.$t('planning_not_empty')];
		},
		isDownloadDisabled() {
			return !this.isExportFormValid || !!this.exportDateRangeError;
		},
	},

	methods: {
		...mapActions({
			logout: 'auth/logout',
			setLocale: 'profile/setLocale',
			exportCsv: 'planning/exportRosterCsv',
			exportXlsx: 'planning/exportRosterXlsx',
		}),
		handleMiniDrawer() {
			localStorage.setItem('drawer', !this.value);
			this.$emit('input', !this.value);
		},

		async handleLogout() {
			await this.logout();
			this.$router.push('/login');
		},
		showDeletedDuties() {
			this.$emit('showDeletedDuties');
		},
		openExportModal(format = 'csv') {
			const today = new Date().toISOString().split('T')[0];
			this.exportFormat = format;
			this.exportDateRange.from = today;
			this.exportDateRange.to = today;
			this.exportDateRangeError = null;
			this.exportDialog = true;
			this.isExportFormValid = false;

			this.$nextTick(() => {
				if (this.$refs.exportForm) {
					this.$refs.exportForm.validate();
				}
			});
		},

		closeExportModal() {
			this.exportDialog = false;
			this.exportDateRange = { from: '', to: '' };
			this.exportDateRangeError = null;
		},

		validateExportDateRange() {
			this.$nextTick(() => {
				this.exportDateRangeError = null;
				if (!this.exportDateRange.from || !this.exportDateRange.to) {
					return;
				}
				const from = new Date(this.exportDateRange.from);
				const to = new Date(this.exportDateRange.to);
				if (from > to) {
					this.exportDateRangeError = this.$t('statisticsOverview.auditExport.invalidDateRange');
				}
			});
		},

		async download() {
			if (!this.$refs.exportForm.validate()) {
				return;
			}
			this.isDownloading = true;
			const from = this.exportDateRange.from;
			const to = this.exportDateRange.to;
			const separator = ';';
			const drivers = JSON.parse(localStorage.getItem('planningDrivers')) ?? [];
			const fileName = `duty-roster-${from}-to-${to}.${this.exportFormat}`;
			if (this.exportFormat === 'csv') {
				const csv = await this.exportCsv({ from, to, separator, drivers });
				this.downloadFile(csv, fileName);
			} else if (this.exportFormat === 'xlsx') {
				const xlsx = await this.exportXlsx({ from, to, drivers });
				this.downloadFile(xlsx, fileName);
			}
			this.closeExportModal();
			this.isDownloading = false;
		},
	},

	mounted() {
		if (localStorage.getItem('drawer') !== null) {
			this.$emit('input', localStorage.getItem('drawer') === 'true');
		}
	},
	watch: {
		'status.process.evaluating'(newVal) {
			if (!this.hasMounted) {
				this.prevEvaluating = newVal;
				this.hasMounted = true;
				return;
			}

			if (newVal) {
				clearTimeout(this.debounceTimer);
				clearTimeout(this.doneTimer);
				this.evaluationComplete = false;
			}

			if (!newVal && this.prevEvaluating === true) {
				clearTimeout(this.debounceTimer);
				this.debounceTimer = setTimeout(() => {
					this.evaluationComplete = true;
					this.doneTimer = setTimeout(() => {
						this.evaluationComplete = false;
					}, 1000);
				}, 100);
			}

			this.prevEvaluating = newVal;
		},
	},
	beforeUnmount() {
		clearTimeout(this.debounceTimer);
		clearTimeout(this.doneTimer);
	},
};
</script>
<style>
.spin {
	animation: spin 2s linear infinite;
	transform-origin: 50% 50%; /* ensures rotation around center */
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(-360deg);
	}
}
</style>
